import { keyframes } from '@emotion/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import { FloorplanAssignment, FloorplanDesk, FloorplanEmployee, FloorplanLevel } from '../../types/floorplan';
import { focusRing, sbg, transition } from '../../theme/sbgTokens';
import { FLOORPLAN_TEAM_HIGHLIGHT, getFloorplanAreaAppearance } from './areaAppearance';
import { safeTeamColor, TEAM, teamColorStyle } from './teamColors';
import { deskNameLines, initialsOf } from './utils';

interface FloorCanvasProps {
  level: FloorplanLevel;
  zoom: number;
  assignmentsByDeskId: Map<string, FloorplanAssignment>;
  autoCenterKey?: string | null;
  selectedDeskId?: string | null;
  selectedGroupDeskIds?: Set<string>;
  highlightedDesk?: { deskId: string; nonce: number } | null;
  pendingDeskIds?: Set<string>;
  onDragStart: (event: React.DragEvent, employee: FloorplanEmployee, sourceDeskId?: string) => void;
  onDeskDrop: (deskId: string, employeeId: string) => void;
  onGroupDrop: (deskId: string) => void;
  onDeskClick: (deskId: string) => void;
  onDeskContextMenu: (deskId: string, position: { x: number; y: number }) => void;
  onZoomChange: (zoom: number) => void;
  /**
   * A team to pick out on the floor. Its desks keep their team color, drawn
   * stronger; every other desk steps back to neutral until it is cleared.
   */
  highlightedTeam?: string | null;
}

const MIN_ZOOM = 0.3;
const MAX_ZOOM = 2;
/** Background grid pitch on the seating view, in floor units. */
const GRID_PITCH = 30;
/** A desk smaller than this on either side shows initials only. */
const COMPACT_DESK = 40;
/** A desk at least this tall has room for a first and last name. */
const TWO_LINE_DESK = 54;

const highlightPulse = keyframes`
  0% { box-shadow: 0 0 0 0 ${sbg.focus.ringSoft}; }
  40% { box-shadow: 0 0 0 10px ${sbg.focus.ringSoft}; }
  100% { box-shadow: 0 0 0 14px transparent; }
`;

interface DeskTileProps {
  desk: FloorplanDesk;
  assignment: FloorplanAssignment | undefined;
  isSelected: boolean;
  isInGroup: boolean;
  isPulsing: boolean;
  isPending: boolean;
  isTeamHighlighted: boolean;
  /** A team is picked on the floor (this desk may or may not be in it). */
  teamFocusActive: boolean;
  registerRef: (deskId: string, node: HTMLDivElement | null) => void;
  onDragStart: FloorCanvasProps['onDragStart'];
  onDeskDrop: FloorCanvasProps['onDeskDrop'];
  onGroupDrop: FloorCanvasProps['onGroupDrop'];
  onDeskClick: FloorCanvasProps['onDeskClick'];
  onDeskContextMenu: FloorCanvasProps['onDeskContextMenu'];
}

interface DeskSurface {
  bg: string;
  border: string;
  ring: string;
  /** Draw the 3px team-color strip along the top edge. */
  strip: boolean;
  /** Another team is picked: this desk steps back (secondary text, no color). */
  dimmed: boolean;
}

/**
 * The desk fill and outline for its state. An assigned desk wears its team's
 * color (teamColors.tsx): a tint, a team-toned outline that clears the floor
 * in both themes, and a strip of the full color; its text stays on text
 * tokens, so it reads on any team color. Open desks differ by a dashed outline
 * and the word "Open". Selection wins over group, group over saving, saving
 * over team color.
 */
const deskSurface = ({ isSelected, isInGroup, isTeamHighlighted, isPending, assigned, teamColor, teamFocusActive }: {
  isSelected: boolean;
  isInGroup: boolean;
  isTeamHighlighted: boolean;
  isPending: boolean;
  assigned: boolean;
  teamColor: string | null;
  teamFocusActive: boolean;
}): DeskSurface => {
  if (isSelected) {
    return { bg: sbg.bg.selected, border: `2px solid ${sbg.border.selected}`, ring: `0 0 0 3px ${sbg.focus.ringSoft}`, strip: false, dimmed: false };
  }
  if (isInGroup) {
    return { bg: sbg.bg.selected, border: `2px dashed ${sbg.border.selected}`, ring: 'none', strip: false, dimmed: false };
  }
  if (isPending) {
    // A saving desk keeps its outline and full-strength text; only the fill shifts.
    return { bg: sbg.bg.hover, border: `1px solid ${sbg.border.strong}`, ring: 'none', strip: false, dimmed: false };
  }
  if (assigned && teamColor) {
    if (isTeamHighlighted) {
      return { bg: TEAM.fillStrong, border: `2px solid ${TEAM.edge}`, ring: 'none', strip: true, dimmed: false };
    }
    if (!teamFocusActive) {
      return { bg: TEAM.fill, border: `1px solid ${TEAM.edge}`, ring: 'none', strip: true, dimmed: false };
    }
    return { bg: sbg.bg.surface, border: `1px solid ${sbg.border.default}`, ring: 'none', strip: false, dimmed: true };
  }
  if (isTeamHighlighted) {
    // A picked team without a usable color falls back to the one blue highlight.
    return { bg: FLOORPLAN_TEAM_HIGHLIGHT.fill, border: FLOORPLAN_TEAM_HIGHLIGHT.border, ring: 'none', strip: false, dimmed: false };
  }
  return {
    bg: sbg.bg.surface,
    border: `1px ${assigned ? 'solid' : 'dashed'} ${teamFocusActive ? sbg.border.default : sbg.border.strong}`,
    ring: 'none',
    strip: false,
    dimmed: teamFocusActive && assigned,
  };
};

/** The team strip plus any ring or hover shadow, as one box-shadow list. */
const deskShadow = (surface: DeskSurface, extra?: string) =>
  [surface.strip ? TEAM.strip : null, extra && extra !== 'none' ? extra : null].filter(Boolean).join(', ') || 'none';

const DeskTile: React.FC<DeskTileProps> = ({
  desk,
  assignment,
  isSelected,
  isInGroup,
  isPulsing,
  isPending,
  isTeamHighlighted,
  teamFocusActive,
  registerRef,
  onDragStart,
  onDeskDrop,
  onGroupDrop,
  onDeskClick,
  onDeskContextMenu,
}) => {
  const employee = assignment?.employee;
  const teamColor = safeTeamColor(employee?.teamColor);
  const canDrag = Boolean(employee?.canEdit);
  const compact = desk.width < COMPACT_DESK || desk.height < COMPACT_DESK;
  const viewOnly = Boolean(employee && !employee.canEdit);
  const surface = deskSurface({
    isSelected,
    isInGroup,
    isTeamHighlighted,
    isPending,
    assigned: Boolean(assignment),
    teamColor,
    teamFocusActive,
  });
  const [firstName, lastName] = deskNameLines(employee?.name ?? '');
  const accessibleName = [
    `Desk ${desk.number}`,
    employee ? employee.name : 'open',
    employee?.teamName || null,
    isSelected ? 'selected' : null,
    isPending ? 'saving' : null,
    viewOnly ? 'view only' : null,
  ].filter(Boolean).join(', ');

  const openContextMenuFromKeyboard = (target: HTMLElement) => {
    const rect = target.getBoundingClientRect();
    onDeskContextMenu(desk.id, { x: rect.left + rect.width / 2, y: rect.bottom });
  };

  const tile = (
    <Box
      ref={(node: HTMLDivElement | null) => registerRef(desk.id, node)}
      data-floorplan-desk="true"
      style={teamColorStyle(teamColor)}
      role="button"
      tabIndex={0}
      aria-label={accessibleName}
      aria-busy={isPending || undefined}
      draggable={canDrag}
      onDragStart={(event: React.DragEvent) => {
        if (employee?.canEdit) {
          onDragStart(event, employee, desk.id);
        }
      }}
      onDragOver={(event: React.DragEvent) => event.preventDefault()}
      onDrop={(event: React.DragEvent) => {
        event.preventDefault();
        const groupToken = event.dataTransfer.getData('application/x-attendance-floorplan-group');
        if (groupToken) {
          onGroupDrop(desk.id);
          return;
        }
        const employeeId = event.dataTransfer.getData('application/x-attendance-floorplan-employee');
        if (employeeId) {
          onDeskDrop(desk.id, employeeId);
        }
      }}
      onClick={() => onDeskClick(desk.id)}
      onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onDeskClick(desk.id);
        } else if (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) {
          event.preventDefault();
          openContextMenuFromKeyboard(event.currentTarget);
        }
      }}
      onContextMenu={(event: React.MouseEvent) => {
        event.preventDefault();
        onDeskContextMenu(desk.id, { x: event.clientX, y: event.clientY });
      }}
      sx={{
        position: 'absolute',
        left: desk.x,
        top: desk.y,
        width: desk.width,
        height: desk.height,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: compact ? 'center' : 'stretch',
        justifyContent: compact ? 'center' : 'flex-start',
        gap: '2px',
        px: compact ? '2px' : '5px',
        py: compact ? '2px' : '4px',
        borderRadius: '6px',
        border: surface.border,
        bgcolor: surface.bg,
        boxShadow: deskShadow(surface, surface.ring),
        overflow: 'hidden',
        cursor: isPending ? 'progress' : canDrag ? 'grab' : 'pointer',
        userSelect: canDrag ? 'none' : 'auto',
        WebkitUserSelect: canDrag ? 'none' : 'auto',
        transition: transition('border-color', 'box-shadow', 'background-color'),
        animation: isPulsing ? `${highlightPulse} 1.6s ${sbg.ease.standard}` : 'none',
        '&:hover': {
          borderColor: sbg.border.selected,
          boxShadow: deskShadow(surface, isSelected ? surface.ring : sbg.shadow.sm),
        },
        '&:focus-visible': focusRing,
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
          ...(isPulsing ? { boxShadow: `0 0 0 4px ${sbg.focus.ringSoft}` } : null),
        },
      }}
    >
      {compact ? (
        <Typography
          component="span"
          variant="labelXs"
          sx={{
            color: employee && !surface.dimmed ? sbg.text.primary : sbg.text.secondary,
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          {employee ? initialsOf(employee.name) : desk.number}
        </Typography>
      ) : (
        <>
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
            <Typography
              component="span"
              variant="labelXs"
              sx={{
                lineHeight: '12px',
                color: isSelected || isInGroup ? sbg.text.accent : sbg.text.secondary,
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {desk.number}
            </Typography>
            {isPending && (
              <CircularProgress size={10} thickness={6} aria-hidden="true" sx={{ flex: 'none', color: sbg.text.secondary }} />
            )}
          </Box>
          {employee ? (
            <Typography
              component="span"
              variant="labelXs"
              sx={{
                color: surface.dimmed ? sbg.text.secondary : sbg.text.primary,
                whiteSpace: 'pre-line',
                overflow: 'hidden',
                overflowWrap: 'anywhere',
                display: '-webkit-box',
                WebkitLineClamp: desk.height >= TWO_LINE_DESK ? 2 : 1,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {lastName ? `${firstName}\n${lastName}` : firstName}
            </Typography>
          ) : (
            <Typography component="span" variant="labelXs" sx={{ color: sbg.text.secondary, mt: 'auto', mb: 'auto' }}>
              Open
            </Typography>
          )}
        </>
      )}
    </Box>
  );

  if (!employee) return tile;

  return (
    <Tooltip
      describeChild
      disableInteractive
      placement="top"
      title={
        <Box component="span" sx={{ display: 'grid' }}>
          <span>{employee.name}</span>
          <span>{[`Desk ${desk.number}`, employee.teamName].filter(Boolean).join(' · ')}</span>
          {viewOnly && <span>View only</span>}
        </Box>
      }
    >
      {tile}
    </Tooltip>
  );
};

const FloorCanvas: React.FC<FloorCanvasProps> = ({
  level,
  zoom,
  assignmentsByDeskId,
  autoCenterKey = null,
  selectedDeskId = null,
  selectedGroupDeskIds = new Set<string>(),
  highlightedDesk = null,
  pendingDeskIds = new Set<string>(),
  onDragStart,
  onDeskDrop,
  onGroupDrop,
  onDeskClick,
  onDeskContextMenu,
  onZoomChange,
  highlightedTeam = null,
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const deskRefs = useRef(new Map<string, HTMLDivElement>());
  const panRafRef = useRef<number | null>(null);
  const panStateRef = useRef({
    isPanning: false,
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });
  const [isPanning, setIsPanning] = useState(false);
  const [pulseDeskId, setPulseDeskId] = useState<string | null>(null);
  const canvasWidth = level.width * zoom;
  const canvasHeight = level.height * zoom;
  const deskKeys = useMemo(() => new Set(level.desks.map((desk) => desk.id)), [level.desks]);

  const registerDeskRef = (deskId: string, node: HTMLDivElement | null) => {
    if (!node) {
      deskRefs.current.delete(deskId);
      return;
    }
    deskRefs.current.set(deskId, node);
  };

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) {
      return undefined;
    }

    const handleMouseMove = (event: MouseEvent) => {
      if (!panStateRef.current.isPanning) {
        return;
      }

      if (panRafRef.current !== null) {
        return;
      }

      panRafRef.current = window.requestAnimationFrame(() => {
        const x = event.pageX - wrapper.offsetLeft;
        const y = event.pageY - wrapper.offsetTop;

        wrapper.scrollLeft = panStateRef.current.scrollLeft - (x - panStateRef.current.startX);
        wrapper.scrollTop = panStateRef.current.scrollTop - (y - panStateRef.current.startY);
        panRafRef.current = null;
      });
    };

    const stopPanning = () => {
      panStateRef.current.isPanning = false;
      setIsPanning(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', stopPanning);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopPanning);
      if (panRafRef.current !== null) {
        window.cancelAnimationFrame(panRafRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!highlightedDesk || !deskKeys.has(highlightedDesk.deskId)) {
      return undefined;
    }

    const wrapper = wrapperRef.current;
    const deskEl = deskRefs.current.get(highlightedDesk.deskId);

    if (!wrapper || !deskEl) {
      return undefined;
    }

    const deskX = deskEl.offsetLeft * zoom;
    const deskY = deskEl.offsetTop * zoom;
    const scrollLeft = deskX - wrapper.clientWidth / 2 + deskEl.clientWidth * zoom;
    const scrollTop = deskY - wrapper.clientHeight / 2 + deskEl.clientHeight * zoom;

    wrapper.scrollTo({
      left: Math.max(0, scrollLeft),
      top: Math.max(0, scrollTop),
      behavior: 'smooth',
    });

    setPulseDeskId(highlightedDesk.deskId);
    const timeout = window.setTimeout(() => setPulseDeskId((current) => (
      current === highlightedDesk.deskId ? null : current
    )), 1600);

    return () => window.clearTimeout(timeout);
  }, [deskKeys, highlightedDesk, zoom]);

  useEffect(() => {
    if (!autoCenterKey) {
      return undefined;
    }

    const wrapper = wrapperRef.current;
    if (!wrapper) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      const centeredLeft = Math.max(0, (canvasWidth - wrapper.clientWidth) / 2);
      const centeredTop = Math.max(0, (canvasHeight - wrapper.clientHeight) / 2);
      wrapper.scrollTo({
        left: centeredLeft,
        top: centeredTop,
        behavior: 'auto',
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [autoCenterKey, canvasHeight, canvasWidth]);

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('[data-floorplan-desk="true"]')) {
      return;
    }

    const wrapper = wrapperRef.current;
    if (!wrapper) {
      return;
    }

    panStateRef.current = {
      isPanning: true,
      startX: event.pageX - wrapper.offsetLeft,
      startY: event.pageY - wrapper.offsetTop,
      scrollLeft: wrapper.scrollLeft,
      scrollTop: wrapper.scrollTop,
    };
    setIsPanning(true);
    event.preventDefault();
  };

  const handleWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (!(event.ctrlKey || event.metaKey)) {
      return;
    }

    event.preventDefault();
    const delta = event.deltaY > 0 ? -0.05 : 0.05;
    onZoomChange(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Number((zoom + delta).toFixed(2)))));
  };

  return (
    <Box
      ref={wrapperRef}
      role="group"
      aria-label={`${level.name} seating map`}
      onMouseDown={handleMouseDown}
      onWheel={handleWheel}
      sx={{
        position: 'relative',
        overflow: 'auto',
        overscrollBehavior: 'contain',
        bgcolor: sbg.bg.surfaceSubtle,
        p: 2,
        height: { xs: 460, md: 'clamp(520px, 66vh, 780px)' },
        cursor: isPanning ? 'grabbing' : 'grab',
        userSelect: isPanning ? 'none' : 'auto',
      }}
    >
      <Box sx={{ width: canvasWidth, height: canvasHeight }}>
        <Box
          sx={{
            position: 'relative',
            width: level.width,
            height: level.height,
            transform: `scale(${zoom})`,
            transformOrigin: 'top left',
            bgcolor: sbg.bg.surface,
            backgroundImage: `linear-gradient(to right, ${sbg.border.subtle} 1px, transparent 1px), linear-gradient(to bottom, ${sbg.border.subtle} 1px, transparent 1px)`,
            backgroundSize: `${GRID_PITCH}px ${GRID_PITCH}px`,
            borderRadius: '8px',
            outline: `1px solid ${sbg.border.default}`,
          }}
        >
          {level.areas.map((area) => {
            const appearance = getFloorplanAreaAppearance(area.type);
            return (
              <Box
                key={area.id}
                title={appearance.hideLabel ? undefined : area.label}
                aria-hidden={appearance.hideLabel || undefined}
                sx={{
                  position: 'absolute',
                  left: area.x,
                  top: area.y,
                  width: area.width,
                  height: area.height,
                  boxSizing: 'border-box',
                  borderRadius: `${appearance.borderRadius}px`,
                  border: appearance.border,
                  bgcolor: appearance.background,
                  px: appearance.hideLabel ? 0 : 1,
                  py: appearance.hideLabel ? 0 : 0.75,
                  display: 'flex',
                  alignItems: appearance.labelAtTop ? 'flex-start' : 'center',
                  justifyContent: appearance.labelAtTop ? 'flex-start' : 'center',
                  textAlign: appearance.labelAtTop ? 'left' : 'center',
                  overflow: 'hidden',
                }}
              >
                {!appearance.hideLabel && (
                  <Typography
                    component="span"
                    variant="labelXs"
                    sx={{ color: appearance.labelColor, lineHeight: 1.2 }}
                  >
                    {area.label}
                  </Typography>
                )}
              </Box>
            );
          })}

          {level.desks.map((desk) => {
            const assignment = assignmentsByDeskId.get(desk.id);
            return (
              <DeskTile
                key={desk.id}
                desk={desk}
                assignment={assignment}
                isSelected={selectedDeskId === desk.id}
                isInGroup={selectedGroupDeskIds.has(desk.id)}
                isPulsing={pulseDeskId === desk.id}
                isPending={pendingDeskIds.has(desk.id)}
                isTeamHighlighted={Boolean(highlightedTeam && assignment?.employee.teamName === highlightedTeam)}
                teamFocusActive={Boolean(highlightedTeam)}
                registerRef={registerDeskRef}
                onDragStart={onDragStart}
                onDeskDrop={onDeskDrop}
                onGroupDrop={onGroupDrop}
                onDeskClick={onDeskClick}
                onDeskContextMenu={onDeskContextMenu}
              />
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default FloorCanvas;
